import React, { useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, FileSignature, Pause, Receipt, RefreshCw, Search, UserRound, ArrowUpRight, Zap } from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import { Stranka, DlazdiceFiltr, Stitek, EmptyState, SkeletonRows, Hlaska, AkceZnovu, Segment, RozbalovaciVyber } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useApiData, apiFetch } from "../lib/api.js";
import { toast } from "../components/Toast.jsx";
import { confirmDialog } from "../components/confirmHost.jsx";
import ClientDetail from "./ClientDetail.jsx";

// Přehled fakturace (16. 10. 2026, Láďa): všechny pravidelné faktury z FAPI na jednom
// místě, s kontrolou proti stavu klientek. Jen owner a CSM.
// Data: GET /api/fakturace-plan?prehled=1.

const fmtKc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;
function fmtDen(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${d}. ${m}. ${y}`;
}
function zaDni(iso) {
  if (!iso) return null;
  const dnes = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
  return Math.round((Date.parse(`${String(iso).slice(0, 10)}T12:00:00Z`) - Date.parse(`${dnes}T12:00:00Z`)) / 86400000);
}
function popisZaDni(n) {
  if (n === null) return "";
  if (n === 0) return "dnes";
  if (n === 1) return "zítra";
  if (n === -1) return "včera";
  if (n < 0) return `před ${-n} dny`;
  return `za ${n} ${n < 5 ? "dny" : "dní"}`;
}
const obdobiText = (m) => (m === 1 ? "Měsíčně" : m === 3 ? "Čtvrtletně" : m === 12 ? "Ročně" : `Po ${m} měsících`);
const STAV_TON = { "Aktivní": "ok", "Přerušeno": "pozor", "Ukončeno": "problem" };

function StavFakturace({ r }) {
  if (!r.ucet) return <Stitek ton="neutralni" velikost="mala">Bez faktury</Stitek>;
  return r.status === "active"
    ? <Stitek ton="ok" velikost="mala">Běží</Stitek>
    : <Stitek ton="pozor" velikost="mala">Pozastavená</Stitek>;
}

// Kontrola jako pilulka; u návrhu dřívější faktury zvlášť pilulka se stavem tréninků (19. 10. 2026).
function KontrolaPilulky({ k }) {
  return (
    <>
      <Stitek ton={k.ton === "problem" ? "problem" : "pozor"} velikost="mala">{k.nadpis}</Stitek>
      {k.stav != null && <Stitek ton="problem" velikost="mala">Stav tréninků {String(k.stav).replace("-", "−")}</Stitek>}
    </>
  );
}

// 1. 11. 2026 (Láďa, schválený mockup 7): návrh dřívější faktury jde vyřídit rovnou z přehledu, vždy s potvrzením.
// Server roli i živý stav tréninků ověřuje znovu (drive_schvalit jen owner, neplatný návrh se nevystaví).
const minus = (n) => String(n).replace("-", "−");
const dnyText = (n) => `${n} ${n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní"}`;
async function postDrive(telo) {
  const r = await apiFetch("/api/fakturace-pravidelna", { method: "POST", body: JSON.stringify(telo) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
function DriveAkce({ r, k, velke, onHotovo }) {
  const [busy, setBusy] = useState(false);
  const d = k.drive;
  const stop = (e) => e.stopPropagation();
  const vystavit = async (e) => {
    stop(e);
    if (busy) return;
    const ok = await confirmDialog({
      title: "Vystavit fakturu dřív?",
      description: `${r.jmeno}, ${r.balicek || ""}. Stav tréninků ${minus(d.stav)} (hranice pro ${d.tydne}T: ${minus(d.prah)}). Příští faktura se vystaví zítra ${fmtDen(d.nove)} místo ${fmtDen(d.dalsi)}, další faktury se posunou o ${dnyText(d.posun)} dřív${d.konecNovy ? ` a konec spolupráce bude ${fmtDen(d.konecNovy)} místo ${fmtDen(d.konec)}` : ""}. Klientce přijde vysvětlující e‑mail. Stav tréninků se před vystavením ještě jednou přepočítá, když se mezitím srovnal, nic se nevystaví.`,
      confirmLabel: "Ano, vystavit zítra", variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try { const j = await postDrive({ akce: "drive_schvalit", id: d.id }); toast(`${r.jmeno}: faktura se vystaví ${fmtDen(j.nove || d.nove)}, e‑mail ${j.email || ""}`, "success"); onHotovo(); }
    catch (err) { toast(`Nepovedlo se: ${err.message}`, "error"); onHotovo(); }
    finally { setBusy(false); }
  };
  const nechat = async (e) => {
    stop(e);
    if (busy) return;
    const ok = await confirmDialog({ title: "Nechat fakturu podle plánu?", description: `${r.jmeno}: faktura zůstane ${fmtDen(d.dalsi)}. Do další faktury se návrh znovu neobjeví.`, confirmLabel: "Nechat podle plánu", variant: "primary" });
    if (!ok) return;
    setBusy(true);
    try { await postDrive({ akce: "drive_odmitnout", id: d.id }); toast(`${r.jmeno}: faktura zůstává podle plánu`, "success"); onHotovo(); }
    catch (err) { toast(`Nepovedlo se: ${err.message}`, "error"); onHotovo(); }
    finally { setBusy(false); }
  };
  return (
    <div className={velke ? "mt-3 grid grid-cols-[1fr_auto] gap-2" : "flex items-center gap-1.5"} onClick={stop}>
      <button type="button" disabled={busy} onClick={vystavit}
        className={`inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 font-bold text-white shadow-sm hover:bg-amber-600 disabled:opacity-60 ${velke ? "py-3 text-[15px]" : "px-3 py-1.5 text-xs"}`}>
        <Zap className={velke ? "h-4 w-4" : "h-3.5 w-3.5"} /> Vystavit dřív
      </button>
      <button type="button" disabled={busy} onClick={nechat}
        className={`rounded-xl border border-slate-200 bg-white font-bold text-brand-700 hover:bg-slate-50 disabled:opacity-60 ${velke ? "px-4 py-3 text-[15px]" : "px-3 py-1.5 text-xs"}`}>
        Nechat
      </button>
    </div>
  );
}

export default function Fakturace() {
  const { data, loading, error, refetch } = useApiData("/api/fakturace-plan?prehled=1");
  const [pohled, setPohled] = useState("kontrola");
  const [hledat, setHledat] = useState("");
  const [stav, setStav] = useState("vse");
  const [vybrana, setVybrana] = useState(null);
  // Karta klientky potřebuje celý řádek klientky (stejný zdroj a cache jako stránka Klienti).
  const { data: klientiData } = useApiData("/api/clients");
  const otevri = (r) => {
    if (!r.notion_id) return;
    const seznam = Array.isArray(klientiData) ? klientiData : (klientiData?.clients || klientiData?.data || []);
    const cela = seznam.find((c) => c.notion_id === r.notion_id);
    setVybrana(cela || { klient: r.jmeno, notion_id: r.notion_id });
  };
  const uzka = useUzkaObrazovka();

  const radky = data?.radky || [];
  const muzeDrive = !!data?.muze_schvalit_drive;
  const s = data?.souhrn || {};
  const filtrovane = useMemo(() => {
    const q = hledat.trim().toLowerCase();
    return radky
      .filter((r) => (pohled === "kontrola" ? (r.kontroly || []).length > 0
        : pohled === "tyden" ? r.status === "active" && zaDni(r.dalsi) !== null && zaDni(r.dalsi) >= 0 && zaDni(r.dalsi) <= 7
        : pohled === "pozastavene" ? r.ucet && r.status !== "active"
        : true))
      .filter((r) => stav === "vse" || r.stav === stav)
      .filter((r) => !q || String(r.jmeno || "").toLowerCase().includes(q))
      .sort((a, b) => (pohled === "kontrola" ? (b.kontroly || []).length - (a.kontroly || []).length : String(a.dalsi || "9999").localeCompare(String(b.dalsi || "9999"))));
  }, [radky, pohled, hledat, stav]);

  // Počty pro filtr stavu (v rámci zvoleného pohledu, bez filtru stavu).
  const vPohledu = useMemo(() => radky.filter((r) => (pohled === "kontrola" ? (r.kontroly || []).length > 0
    : pohled === "tyden" ? r.status === "active" && zaDni(r.dalsi) !== null && zaDni(r.dalsi) >= 0 && zaDni(r.dalsi) <= 7
    : pohled === "pozastavene" ? r.ucet && r.status !== "active" : true)), [radky, pohled]);
  const pocet = (st) => vPohledu.filter((r) => st === "vse" || r.stav === st).length;
  const tydenUcty = s.tyden?.ucty || [];

  return (
    <Stranka>
      <GradientHeader
        title="Fakturace"
        icon={Receipt}
        loading={loading}
        napoveda="Pravidelné faktury ze všech účtů FAPI a jejich kontrola proti stavu klientek. Fakturu vystavuje FAPI, kdy a jakou řídí admin."
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <DlazdiceFiltr popisek="Ke kontrole" hodnota={s.kontrola ?? "—"} ikona={AlertTriangle} ton="problem"
          info={<InfoTip text="Faktura nesedí se stavem klientky: přerušená nebo ukončená, které faktura běží, aktivní bez faktury, víc faktur na jednu klientku, jiný balíček než na faktuře." />}
          zapnuto={pohled === "kontrola"} onClick={() => setPohled("kontrola")} />
        <DlazdiceFiltr popisek="Příštích 7 dní" hodnota={s.tyden ? `${s.tyden.pocet}×` : "—"} ikona={CalendarClock} ton="znacka"
          info={<InfoTip text={s.tyden ? `Faktury, které FAPI vystaví v příštích 7 dnech, celkem ${fmtKc(s.tyden.castka)}.` : "Faktury v příštích 7 dnech."} />}
          zapnuto={pohled === "tyden"} onClick={() => setPohled("tyden")} />
        <DlazdiceFiltr popisek="Pozastavené" hodnota={s.pozastaveno ?? "—"} ikona={Pause} ton="pozor"
          zapnuto={pohled === "pozastavene"} onClick={() => setPohled("pozastavene")} />
        <DlazdiceFiltr popisek="Smlouva k podpisu" hodnota={(data?.smlouvy || []).length} ikona={FileSignature} ton="problem"
          info={<InfoTip text="Klientky uzavřené po telefonu, které mají smlouvu podepsat na prvním tréninku a trenér to ještě nepotvrdil." />}
          zapnuto={pohled === "smlouvy"} onClick={() => setPohled("smlouvy")} />
        <DlazdiceFiltr popisek="Fakturace běží" hodnota={s.bezi ?? "—"} ikona={CheckCircle2} ton="ok"
          info={<InfoTip text={s.mesicne ? `Pravidelně fakturujeme ${fmtKc(s.mesicne)} měsíčně.` : "Aktivní pravidelné faktury."} />}
          zapnuto={pohled === "vse"} onClick={() => setPohled("vse")} />
      </div>

      {pohled === "smlouvy" ? (
        <Card title="Smlouva k podpisu" ikona={FileSignature} lista odznak={`${(data?.smlouvy || []).length}`}>
          {(data?.smlouvy || []).length === 0 ? <EmptyState icon={CheckCircle2} title="Všechny smlouvy jsou podepsané" hint="Žádná klientka nečeká na podpis smlouvy." /> : (
            <div className="space-y-2">
              {(data?.smlouvy || []).map((m) => {
                const po = m.prvni_trenink ? -zaDni(m.prvni_trenink) : null;
                return (
                  <div key={m.deal_id} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${po > 0 ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 demo-sensitive">{m.jmeno}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <Stitek ton={po > 0 ? "problem" : po === 0 ? "pozor" : "neutralni"} velikost="mala">{po === null ? "1. trénink nezapsaný" : po > 0 ? `${po} ${po === 1 ? "den" : po < 5 ? "dny" : "dní"} po 1. tréninku` : po === 0 ? "1. trénink dnes" : `1. trénink ${fmtDen(m.prvni_trenink)}`}</Stitek>
                        <Stitek ton="neutralni" velikost="mala"><UserRound className="mr-1 inline h-3 w-3" />{m.trener}</Stitek>
                        <Stitek ton="neutralni" velikost="mala">{m.balicek}</Stitek>
                        {m.alert && <Stitek ton="pozor" velikost="mala">Slack Michalovi a Kristině {fmtDen(m.alert)}</Stitek>}
                      </div>
                    </div>
                    <button type="button" className="btn-ikona" aria-label={`Otevřít kartu ${m.jmeno}`} title="Otevřít kartu" onClick={() => otevri(m)}><ArrowUpRight className="h-5 w-5" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      ) : (
      <Card title="Klientky" ikona={Receipt} lista odznak={data ? `${filtrovane.length}` : undefined}>
        <div className="mb-3 space-y-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="Hledat klientku…" aria-label="Hledat klientku" className="field pl-9" />
          </div>
          {uzka ? (
            <RozbalovaciVyber stitek="Stav" value={stav} onChange={setStav} aria-label="Stav klientky"
              items={[["vse", "Všechny"], ["Aktivní", "Aktivní"], ["Přerušeno", "Přerušeno"], ["Ukončeno", "Ukončeno"]].map(([key, nazev]) => ({ key, label: `${nazev} (${pocet(key)})` }))} />
          ) : (
            <Segment value={stav} onChange={setStav} aria-label="Stav klientky"
            items={[["vse", "Vše"], ["Aktivní", "Aktivní"], ["Přerušeno", "Přerušeno"], ["Ukončeno", "Ukončeno"]].map(([key, nazev]) => ({ key, label: <span>{nazev}<span className="hidden tabular-nums sm:inline"> {pocet(key)}</span></span> }))} />
          )}
        </div>

        {/* 17. 10. 2026 (Láďa): kolik se v příštích 7 dnech vyfakturuje a na kterém účtu FAPI. */}
        {pohled === "tyden" && s.tyden && (
          <div className="mb-3 rounded-2xl border border-slate-200 bg-brand-50/50 p-3">
            <div className="text-sm text-slate-700">
              V příštích 7 dnech FAPI vystaví <b className="text-slate-900">{s.tyden.pocet} {s.tyden.pocet === 1 ? "fakturu" : s.tyden.pocet < 5 ? "faktury" : "faktur"}</b> za <b className="text-slate-900 demo-sensitive">{fmtKc(s.tyden.castka)}</b>.
            </div>
            {tydenUcty.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {tydenUcty.map((u) => (
                  <div key={u.ucet} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Účet {u.ucet}</div>
                    <div className="text-base font-bold tabular-nums text-slate-900 demo-sensitive">{fmtKc(u.castka)}</div>
                    <div className="text-xs text-slate-500">{u.pocet} {u.pocet === 1 ? "faktura" : u.pocet < 5 ? "faktury" : "faktur"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <Hlaska ton="problem" title="Fakturace se nenačetla" akce={<AkceZnovu onClick={refetch} />}>{String(error)}</Hlaska>}

        {loading && !data ? <SkeletonRows rows={6} /> : !error && filtrovane.length === 0 ? (
          <EmptyState icon={CheckCircle2} title={pohled === "kontrola" ? "Vše sedí" : "Nic tu není"} hint={pohled === "kontrola" ? "Žádná faktura nesedí špatně se stavem klientky." : "Zkus jiný pohled."} />
        ) : uzka ? (
          <div className="space-y-2">
            {filtrovane.map((r) => (
              <div key={r.notion_id || r.periodic_id} role="button" tabIndex={0} onClick={() => otevri(r)}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) otevri(r); }}
                className={`polozka w-full cursor-pointer text-left active:bg-brand-light ${(r.kontroly || []).length ? "border-amber-200 bg-amber-50/40" : ""}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 demo-sensitive">{r.jmeno}</div>
                    <div className="text-xs text-slate-500">{r.balicek || "—"}</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Stitek ton={STAV_TON[r.stav] || "neutralni"} velikost="mala">{r.stav || "?"}</Stitek>
                    <StavFakturace r={r} />
                  </div>
                </div>
                {r.ucet && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Stitek ton="neutralni" velikost="mala"><span className="demo-sensitive">{fmtKc(r.castka_mesic)} / měsíc</span></Stitek>
                    <Stitek ton="neutralni" velikost="mala">{obdobiText(r.mesicu)}, {r.ucet}</Stitek>
                    {r.status === "active" && <Stitek ton="neutralni" velikost="mala">Příští {fmtDen(r.dalsi)}, {popisZaDni(zaDni(r.dalsi))}</Stitek>}
                  </div>
                )}
                {(r.kontroly || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(r.kontroly || []).map((k, i) => <KontrolaPilulky key={i} k={k} />)}
                  </div>
                )}
                {muzeDrive && (r.kontroly || []).filter((k) => k.drive).map((k) => <DriveAkce key={k.drive.id} r={r} k={k} velke onHotovo={refetch} />)}
              </div>
            ))}
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-brand-50/60 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 text-left font-semibold sm:px-5">Klientka</th>
                  <th className="px-2 py-3 text-left font-semibold">Stav</th>
                  <th className="px-2 py-3 text-left font-semibold">Fakturace</th>
                  <th className="px-2 py-3 text-right font-semibold">Částka / měsíc</th>
                  <th className="hidden px-2 py-3 text-left font-semibold lg:table-cell">Období</th>
                  <th className="px-2 py-3 text-left font-semibold">Příští faktura</th>
                  <th className="px-2 py-3 text-left font-semibold">Kontrola</th>
                </tr>
              </thead>
              <tbody>
                {filtrovane.map((r) => (
                  <tr key={r.notion_id || r.periodic_id} onClick={() => otevri(r)}
                    className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-brand-50/50 ${(r.kontroly || []).length ? "bg-amber-50/30" : ""}`}>
                    <td className="px-4 py-3 sm:px-5">
                      <div className="font-semibold text-slate-900 demo-sensitive">{r.jmeno}</div>
                      <div className="text-xs text-slate-500">{r.balicek || "—"}</div>
                    </td>
                    <td className="px-2 py-3"><Stitek ton={STAV_TON[r.stav] || "neutralni"} velikost="mala">{r.stav || "?"}</Stitek></td>
                    <td className="px-2 py-3"><StavFakturace r={r} />{r.ucet && <div className="mt-0.5 text-xs text-slate-400">{r.ucet}</div>}</td>
                    <td className="px-2 py-3 text-right font-bold tabular-nums text-slate-900 demo-sensitive">{r.ucet ? fmtKc(r.castka_mesic) : <span className="font-normal text-slate-400">—</span>}</td>
                    <td className="hidden px-2 py-3 text-slate-700 lg:table-cell">{r.ucet ? obdobiText(r.mesicu) : "—"}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-slate-700">
                      {r.ucet && r.status === "active" ? (<div><div>{fmtDen(r.dalsi)}</div><div className="text-xs text-slate-400">{popisZaDni(zaDni(r.dalsi))}</div></div>) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-3">
                      {(r.kontroly || []).length
                        ? <div className="flex flex-wrap items-center gap-1">{(r.kontroly || []).map((k, i) => <KontrolaPilulky key={i} k={k} />)}
                            {muzeDrive && (r.kontroly || []).filter((k) => k.drive).map((k) => <DriveAkce key={k.drive.id} r={r} k={k} onHotovo={refetch} />)}</div>
                        : <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sedí</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {vybrana && <ClientDetail client={vybrana} onClose={() => setVybrana(null)} otevritFakturaci />}
    </Stranka>
  );
}
